import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Shield, CloudUpload, CheckCircle2, AlertTriangle, RefreshCw, Send, ExternalLink, Clock, Loader2, Download, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SiteVault panel — full-site archive to Google Drive.
 *
 * - Shows Drive connection status (mock / real / error)
 * - Retention policy summary
 * - Last job + full step timeline
 * - "Run now" button with scope picker (full / db_only / audio_only)
 * - Recent job history with clickable Drive links per uploaded file
 */
export default function VaultPanel() {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState('');
  const [liveProgress, setLiveProgress] = useState(null);
  const [testingDrive, setTestingDrive] = useState(false);
  const [showSteps, setShowSteps] = useState(null); // job id
  // Restore-from-Drive modal state
  const [restoreFolder, setRestoreFolder] = useState('weekly_gold');
  const [restoreFiles, setRestoreFiles] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restorePickedId, setRestorePickedId] = useState('');
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const loadDriveFiles = async (folder) => {
    setRestoreLoading(true);
    setRestoreFiles(null);
    setRestorePickedId('');
    try {
      const r = await api.get(`/sitevault/drive-files?folder=${folder}`);
      setRestoreFiles(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to list Drive files');
    } finally {
      setRestoreLoading(false);
    }
  };

  const openRestoreModal = async () => {
    setShowRestoreModal(true);
    setRestoreResult(null);
    setRestoreConfirm('');
    await loadDriveFiles(restoreFolder);
  };

  const runRestore = async () => {
    if (!restorePickedId) return toast.error('Pick a snapshot to restore');
    if (restoreConfirm !== 'RESTORE-SCALEBOARD') return toast.error('Type RESTORE-SCALEBOARD exactly');
    setRestoreRunning(true);
    try {
      const r = await api.post('/sitevault/restore-from-drive', {
        file_id: restorePickedId,
        confirm: restoreConfirm,
      });
      setRestoreResult(r.data);
      toast.success(`Restored ${r.data.restored_total_documents} docs across ${r.data.restored_collections} collections`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreRunning(false);
    }
  };

  const load = async () => {
    try {
      const [s, j] = await Promise.all([
        api.get('/sitevault/status'),
        api.get('/sitevault/jobs?limit=20'),
      ]);
      setStatus(s.data);
      setJobs(j.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load SiteVault status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000); // auto-refresh every 20s to catch cron jobs
    return () => clearInterval(iv);
  }, []);

  // Poll /sitevault/current every 2s so the dashboard can render a live
  // progress pill ("Building archive… 347 files · 523 MB") while a manual
  // Run-Now is in flight. Stops as soon as the backend reports running=false.
  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await api.get('/sitevault/current');
        if (!alive) return;
        if (r.data?.running) {
          setLiveProgress(r.data);
          timer = setTimeout(tick, 2000);
        } else {
          if (liveProgress) {
            // Transition from running → idle: refresh jobs list so the new
            // run appears in Recent Jobs with its Drive links.
            load();
          }
          setLiveProgress(null);
          timer = setTimeout(tick, 5000); // poll less aggressively when idle
        }
      } catch {
        timer = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runNow = async (scope) => {
    setRunning(scope);
    try {
      await api.post('/sitevault/run-now', { scope });
      toast.success(
        scope === 'full'
          ? 'Comprehensive archive queued — watch progress below'
          : `Queued · scope=${scope}. Results appear in Recent Jobs within ~30s.`
      );
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Queue failed');
    } finally {
      setRunning('');
    }
  };

  const testDrive = async () => {
    setTestingDrive(true);
    try {
      const r = await api.post('/sitevault/test-drive');
      if (r.data?.ok) {
        toast.success(`Connected · ${r.data.email} · folder: ${r.data.parent_folder_name}`);
      } else {
        toast.error(r.data?.error || 'Drive not reachable');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTestingDrive(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="vault-loading">
        <Loader2 className="animate-spin" style={{ color: '#2A7B88' }} />
      </div>
    );
  }

  const isMock = status?.mock_mode;
  const driveOk = status?.drive?.ok;

  return (
    <div className="space-y-4" data-testid="vault-panel">
      {/* Header + status */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: driveOk ? '#ECFDF5' : (isMock ? '#FEF3E5' : '#FEF2F2') }}>
              <Shield size={18} style={{ color: driveOk ? '#2D8B5E' : (isMock ? '#8A5A00' : '#D4726A') }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>SiteVault</h3>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                Full-system archive to Google Drive — DB + code + docs + audio.
              </p>
            </div>
          </div>
          <button onClick={load}
            className="px-2.5 py-1.5 rounded-md border text-[11px] inline-flex items-center gap-1"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            data-testid="vault-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {/* Drive connection banner */}
        {isMock ? (
          <div className="rounded-lg p-3 mb-4 flex items-start gap-2"
            style={{ backgroundColor: '#FEF3E5', color: '#8A5A00' }}
            data-testid="vault-mock-banner">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div className="text-[11px] flex-1">
              <p className="font-semibold mb-0.5">Running in MOCK mode</p>
              <p>
                Uploads are logged and emails are sent with placeholder links. To activate real Google Drive archival:
              </p>
              <ol className="list-decimal pl-4 mt-1 space-y-0.5">
                <li>Create a service account in <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline">Google Cloud Console</a> → download JSON key</li>
                <li>Create a folder on Google Drive (e.g. "ScaleBoard Archive") → right-click → Share → paste the service account email → Editor access</li>
                <li>Copy the folder ID from the Drive URL (<code className="px-1 rounded" style={{ backgroundColor: '#FFF' }}>drive.google.com/drive/folders/<b>FOLDER_ID</b></code>)</li>
                <li>Paste both into <code className="px-1 rounded" style={{ backgroundColor: '#FFF' }}>backend/.env</code> as <code>GDRIVE_SERVICE_KEY</code> (one line) and <code>GDRIVE_PARENT_FOLDER_ID</code></li>
                <li>Restart backend — SiteVault auto-detects and switches to real mode</li>
              </ol>
            </div>
          </div>
        ) : driveOk ? (
          <div className="rounded-lg p-3 mb-4 flex items-start gap-2"
            style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
            <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
            <div className="text-[11px] flex-1">
              <p className="font-semibold mb-0.5">Connected to Google Drive</p>
              <p style={{ color: '#1B2A4A' }}>
                Service account: <code className="px-1 rounded font-mono" style={{ backgroundColor: '#FFF' }}>{status.drive.email}</code> · Folder: <b>{status.drive.parent_folder_name}</b>
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-lg p-3 mb-4 flex items-start gap-2"
            style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <p className="text-[11px]"><b>Drive connection failed:</b> {status?.drive?.error}</p>
          </div>
        )}

        {/* Retention + schedule summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="rounded border p-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Daily DB</p>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{status?.retention?.daily_db_days || 30} days</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>04:00 IST</p>
          </div>
          <div className="rounded border p-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Weekly Gold</p>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{status?.retention?.weekly_gold_weeks || 12} weeks</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Mon 00:05 IST</p>
          </div>
          <div className="rounded border p-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Source ZIP</p>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{status?.retention?.weekly_source_weeks || 12} weeks</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Mon 03:00 IST</p>
          </div>
          <div className="rounded border p-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Audio ZIPs</p>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{status?.retention?.weekly_audio_weeks || 4} weeks</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Mon 03:00 IST</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 items-center pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
          <button onClick={() => runNow('full')} disabled={!!running}
            className="px-3 py-2 rounded-md text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ backgroundColor: '#2A7B88' }}
            title="Build a comprehensive /app archive (~1 GB) and upload to Google Drive. Watch live progress below."
            data-testid="vault-run-full">
            {running === 'full' ? <Loader2 size={12} className="animate-spin" /> : <CloudUpload size={12} />}
            Full archive now
          </button>
          <button onClick={() => runNow('db_only')} disabled={!!running}
            className="px-3 py-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
            data-testid="vault-run-db">
            {running === 'db_only' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            DB snapshot only
          </button>
          <button onClick={() => runNow('audio_only')} disabled={!!running}
            className="px-3 py-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
            data-testid="vault-run-audio">
            {running === 'audio_only' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Audio ZIPs only
          </button>
          <button onClick={testDrive} disabled={testingDrive}
            className="px-3 py-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            data-testid="vault-test-drive">
            {testingDrive ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Test Drive connection
          </button>
          <button onClick={openRestoreModal} disabled={isMock}
            className="px-3 py-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ borderColor: '#D4726A', color: '#D4726A', backgroundColor: '#FEF2F2' }}
            title={isMock ? 'Drive creds required to restore' : 'Pull a past snapshot from Drive and replay it into MongoDB'}
            data-testid="vault-restore-btn">
            <RotateCcw size={12} /> Restore from Drive
          </button>
          <p className="text-[10px] ml-1" style={{ color: '#9CA3AF' }}>
            Email notifications → <b>{status?.email_to}</b>
          </p>
        </div>

        {/* Live progress panel — polled every 2s while a run is active */}
        {liveProgress && (
          <div className="mt-3 rounded-lg border p-3 flex flex-col gap-2"
               style={{ borderColor: '#2A7B88', backgroundColor: '#F0FAFB' }}
               data-testid="vault-live-progress">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" style={{ color: '#2A7B88' }} />
                <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
                  {liveProgress.current_step === 'build_source_zip'
                    ? 'Building comprehensive archive'
                    : `Running · ${liveProgress.scope || 'full'}`}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                      style={{ backgroundColor: 'white', color: '#2A7B88', border: '1px solid #2A7B88' }}>
                  {liveProgress.elapsed_seconds?.toFixed?.(0) ?? liveProgress.elapsed_seconds}s
                </span>
              </div>
              <div className="text-[11px] font-mono" style={{ color: '#1B2A4A' }}>
                <b data-testid="vault-progress-files">{liveProgress.files_done || 0}</b> files ·{' '}
                <b data-testid="vault-progress-mb">{liveProgress.mb_packed || 0}</b> MB packed
              </div>
            </div>
            {/* Step breadcrumb — shows what's finished so far */}
            {liveProgress.steps_done_names?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {liveProgress.steps_done_names.map((s, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: 'white', color: '#2D8B5E', border: '1px solid #D1FAE5' }}>
                    ✓ {s}
                  </span>
                ))}
                {liveProgress.current_step && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{ backgroundColor: 'white', color: '#2A7B88', border: '1px solid #2A7B88' }}>
                    ◌ {liveProgress.current_step}…
                  </span>
                )}
              </div>
            )}
            <p className="text-[10px]" style={{ color: '#6B7280' }}>
              Triggered by <code>{liveProgress.triggered_by}</code>. The archive uploads to Google Drive and then emails you the links — keep this tab open or come back in a minute.
            </p>
          </div>
        )}
      </div>

      {/* Recent jobs table */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }} data-testid="vault-jobs">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} style={{ color: '#2A7B88' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Recent Jobs</h3>
          <span className="text-[10px]" style={{ color: '#9CA3AF' }}>· last 20</span>
        </div>

        {jobs.length === 0 ? (
          <p className="text-center text-[11px] py-4" style={{ color: '#9CA3AF' }}>No jobs yet. Click "Full archive now" to run one.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: '#F3F0EB' }}>
            {jobs.map((j) => {
              const started = j.started_at ? new Date(j.started_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—';
              const expanded = showSteps === j._id;
              const failed = (j.steps || []).some((s) => !s.ok);
              return (
                <div key={j._id} className="py-2 text-xs" data-testid={`vault-job-${j._id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      {failed ? <AlertTriangle size={12} style={{ color: '#D4726A' }} /> : <CheckCircle2 size={12} style={{ color: '#2D8B5E' }} />}
                      <span className="font-mono text-[10px]" style={{ color: '#9CA3AF' }}>{started}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                        style={{ backgroundColor: j.scope === 'full' ? '#F0FAFB' : '#F3F0EB', color: '#2A7B88' }}>
                        {j.scope}
                      </span>
                      <span className="text-[11px]" style={{ color: '#1B2A4A' }}>
                        {j.uploaded_count} file{j.uploaded_count !== 1 ? 's' : ''} · {j.total_uploaded_mb || 0} MB · {j.duration_seconds || 0}s
                      </span>
                      <span className="text-[10px]" style={{ color: '#9CA3AF' }}>by {j.triggered_by}</span>
                    </div>
                    <button onClick={() => setShowSteps(expanded ? null : j._id)}
                      className="text-[10px] px-2 py-0.5 rounded border"
                      style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                      data-testid={`vault-job-toggle-${j._id}`}>
                      {expanded ? 'Hide' : 'Show'} details
                    </button>
                  </div>
                  {expanded && (
                    <div className="mt-2 pl-5 space-y-1 text-[11px]">
                      <p className="font-semibold" style={{ color: '#1B2A4A' }}>Uploaded</p>
                      {(j.uploaded_files || []).length === 0 && <p style={{ color: '#9CA3AF' }}>No files uploaded</p>}
                      {(j.uploaded_files || []).map((u, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate">
                            <span className="text-[9px] px-1 rounded mr-1" style={{ backgroundColor: '#F3F0EB', color: '#6B7280' }}>{u.category}</span>
                            {u.name} · {u.size_mb} MB
                          </span>
                          {u.web_link && <a href={u.web_link} target="_blank" rel="noreferrer" className="text-[10px] inline-flex items-center gap-0.5" style={{ color: '#2A7B88' }}>
                            Open <ExternalLink size={9} />
                          </a>}
                        </div>
                      ))}
                      <p className="font-semibold mt-2" style={{ color: '#1B2A4A' }}>Steps</p>
                      {(j.steps || []).map((s, i) => (
                        <div key={i} className="flex items-center gap-2 font-mono text-[10px]">
                          <span style={{ color: s.ok ? '#2D8B5E' : '#D4726A' }}>{s.ok ? '✓' : '✗'}</span>
                          <span style={{ color: '#6B7280' }}>{s.name}</span>
                          <span style={{ color: '#9CA3AF' }}>{s.seconds}s</span>
                          {s.error && <span style={{ color: '#D4726A' }}>· {s.error}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Restore-from-Drive modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => !restoreRunning && setShowRestoreModal(false)}
          data-testid="vault-restore-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex items-center gap-2">
                <RotateCcw size={16} style={{ color: '#D4726A' }} />
                <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Restore from Drive</h3>
              </div>
              <button onClick={() => setShowRestoreModal(false)} disabled={restoreRunning}
                className="text-lg" style={{ color: '#9CA3AF' }}>✕</button>
            </div>

            {restoreResult ? (
              <div className="p-5 text-center">
                <CheckCircle2 size={36} className="mx-auto mb-3" style={{ color: '#2D8B5E' }} />
                <p className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Restore complete</p>
                <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                  Restored <b>{restoreResult.restored_total_documents?.toLocaleString('en-IN')}</b> documents
                  across <b>{restoreResult.restored_collections}</b> collections from <code className="font-mono">{restoreResult.restored_from}</code>.
                </p>
                <button onClick={() => { setShowRestoreModal(false); setRestoreResult(null); }}
                  className="mt-4 px-4 py-2 rounded-md text-xs font-semibold text-white"
                  style={{ backgroundColor: '#2A7B88' }}
                  data-testid="vault-restore-done">
                  Done
                </button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div className="rounded-lg p-3 flex items-start gap-2" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <p className="text-[11px]">
                    <b>Destructive operation.</b> This REPLACES every collection in the current MongoDB (except system collections).
                    Existing data will be deleted and replaced with the snapshot contents. Audio files in GridFS are preserved.
                  </p>
                </div>

                {/* Folder picker */}
                <div>
                  <label className="text-[11px] font-semibold" style={{ color: '#1B2A4A' }}>Choose category</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[
                      { id: 'weekly_gold', label: 'Weekly Gold Masters (recommended)' },
                      { id: 'daily_db', label: 'Daily DB Snapshots' },
                    ].map((f) => (
                      <button key={f.id}
                        onClick={() => { setRestoreFolder(f.id); loadDriveFiles(f.id); }}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium border"
                        style={{
                          borderColor: restoreFolder === f.id ? '#2A7B88' : '#E5E1DC',
                          backgroundColor: restoreFolder === f.id ? '#F0FAFB' : '#FFF',
                          color: restoreFolder === f.id ? '#2A7B88' : '#6B7280',
                        }}
                        data-testid={`vault-restore-folder-${f.id}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* File list */}
                <div>
                  <label className="text-[11px] font-semibold" style={{ color: '#1B2A4A' }}>
                    Pick the snapshot to restore {restoreFiles ? `(${restoreFiles.items?.length || 0} available)` : ''}
                  </label>
                  <div className="mt-1 border rounded-md max-h-48 overflow-y-auto" style={{ borderColor: '#E5E1DC' }}>
                    {restoreLoading && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={14} className="animate-spin" style={{ color: '#2A7B88' }} />
                      </div>
                    )}
                    {restoreFiles?.mock_mode && (
                      <p className="p-3 text-[11px]" style={{ color: '#8A5A00' }}>
                        ⚠️ Drive not configured yet — no files to restore. Set GDRIVE_SERVICE_KEY + GDRIVE_PARENT_FOLDER_ID in backend/.env to activate.
                      </p>
                    )}
                    {!restoreLoading && restoreFiles?.items?.length === 0 && !restoreFiles?.mock_mode && (
                      <p className="p-3 text-[11px]" style={{ color: '#9CA3AF' }}>
                        No files in this folder yet — wait for the next cron run or hit "Full archive now" first.
                      </p>
                    )}
                    {restoreFiles?.items?.map((f) => (
                      <label key={f.id} className="flex items-center gap-2 px-2 py-2 border-b cursor-pointer hover:bg-[#F7F5F2]"
                        style={{ borderColor: '#F3F0EB' }}
                        data-testid={`vault-restore-file-${f.id}`}>
                        <input type="radio" name="restore-file" value={f.id}
                          checked={restorePickedId === f.id}
                          onChange={() => setRestorePickedId(f.id)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{f.name}</p>
                          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                            {f.size_mb} MB · Created {f.created_at ? new Date(f.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Confirm phrase */}
                <div>
                  <label className="text-[11px] font-semibold" style={{ color: '#1B2A4A' }}>
                    Type <code className="px-1 rounded" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>RESTORE-SCALEBOARD</code> to confirm
                  </label>
                  <input type="text" value={restoreConfirm}
                    onChange={(e) => setRestoreConfirm(e.target.value)}
                    placeholder="RESTORE-SCALEBOARD"
                    className="w-full text-xs px-2 py-1.5 rounded border font-mono mt-1"
                    style={{ borderColor: restoreConfirm === 'RESTORE-SCALEBOARD' ? '#2D8B5E' : '#E5E1DC' }}
                    data-testid="vault-restore-confirm" />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: '#F3F0EB' }}>
                  <button onClick={() => setShowRestoreModal(false)} disabled={restoreRunning}
                    className="px-3 py-1.5 rounded-md border text-xs font-medium"
                    style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                    Cancel
                  </button>
                  <button onClick={runRestore}
                    disabled={restoreRunning || !restorePickedId || restoreConfirm !== 'RESTORE-SCALEBOARD'}
                    className="px-4 py-1.5 rounded-md text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ backgroundColor: '#D4726A' }}
                    data-testid="vault-restore-run">
                    {restoreRunning ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {restoreRunning ? 'Restoring…' : 'Restore now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
